import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle, LogIn, UserPlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import CustomerDashboard from './CustomerDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthModal = ({ isOpen, onClose, onLoginSuccess, initialCustomerData }) => {
  // Check for existing session first
  const getInitialState = () => {
    const savedCustomer = sessionStorage.getItem('neura_customer');
    if (savedCustomer) {
      try {
        return { mode: 'dashboard', data: JSON.parse(savedCustomer) };
      } catch (e) {
        sessionStorage.removeItem('neura_customer');
      }
    }
    return { mode: 'login', data: initialCustomerData || null };
  };
  
  const initialState = getInitialState();
  const [mode, setMode] = useState(initialState.mode);
  const [customerData, setCustomerData] = useState(initialState.data);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Re-check session when modal opens (in case it changed)
  useEffect(() => {
    if (isOpen && mode !== 'dashboard') {
      const savedCustomer = sessionStorage.getItem('neura_customer');
      if (savedCustomer) {
        try {
          const data = JSON.parse(savedCustomer);
          setCustomerData(data);
          setMode('dashboard');
        } catch (e) {
          sessionStorage.removeItem('neura_customer');
        }
      }
    }
  }, [isOpen, mode]);

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };
  
  const handleLogout = () => {
    sessionStorage.removeItem('neura_customer');
    setCustomerData(null);
    setMode('login');
    resetForm();
  };
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // When modal closes, check if we should reset to login mode
      const savedCustomer = sessionStorage.getItem('neura_customer');
      if (!savedCustomer) {
        setMode('login');
        setCustomerData(null);
        resetForm();
      }
    }
  }, [isOpen]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!name.trim()) {
      setError('Please enter your full name');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/customers/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Success - store token and show dashboard
      setSuccess('Account created successfully!');
      
      const userData = {
        token: data.access_token,
        clientId: data.client_id,
        name: data.name,
        email: data.email,
        isCustomer: true
      };
      
      sessionStorage.setItem('neura_customer', JSON.stringify(userData));
      setCustomerData(userData);
      
      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
      
      setTimeout(() => {
        setMode('dashboard');
      }, 500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/customers/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Success - show dashboard
      setSuccess('Login successful!');
      
      const userData = {
        token: data.access_token,
        clientId: data.client_id,
        name: data.name,
        email: data.email,
        isCustomer: true
      };
      
      sessionStorage.setItem('neura_customer', JSON.stringify(userData));
      setCustomerData(userData);
      
      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
      
      setTimeout(() => {
        setMode('dashboard');
      }, 300);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  
  // Show Customer Dashboard if logged in
  if (mode === 'dashboard' && customerData) {
    return (
      <div 
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="auth-modal-backdrop"
      >
        <div 
          className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="customer-dashboard-modal"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white transition-colors"
            data-testid="auth-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          <CustomerDashboard 
            customerData={customerData} 
            onLogout={handleLogout}
          />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="auth-modal-backdrop"
    >
      <div 
        className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid="auth-modal"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="auth-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              {mode === 'signup' ? (
                <UserPlus className="w-6 h-6 text-white" />
              ) : (
                <LogIn className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {mode === 'signup' ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-white/80 text-sm">
                {mode === 'signup' ? 'Join NEURA AI today' : 'Sign in to your account'}
              </p>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => switchMode('login')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'login'
                ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="auth-tab-login"
          >
            Sign In
          </button>
          <button
            onClick={() => switchMode('signup')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'signup'
                ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="auth-tab-signup"
          >
            Create Account
          </button>
        </div>

        {/* Form */}
        <form 
          onSubmit={mode === 'signup' ? handleSignup : handleLogin}
          className="p-6 space-y-4"
        >
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="auth-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="auth-success">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* Name Field (Signup only) */}
          {mode === 'signup' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10"
                  data-testid="auth-name-input"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-10"
                data-testid="auth-email-input"
                disabled={loading}
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'Min 8 characters' : 'Enter password'}
                className="pl-10 pr-10"
                data-testid="auth-password-input"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm Password (Signup only) */}
          {mode === 'signup' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="pl-10"
                  data-testid="auth-confirm-password-input"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-2.5"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {mode === 'signup' ? 'Creating Account...' : 'Signing In...'}
              </span>
            ) : (
              mode === 'signup' ? 'Create Account' : 'Sign In'
            )}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-500">
                {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
              </span>
            </div>
          </div>

          {/* Switch Mode Link */}
          <button
            type="button"
            onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}
            className="w-full text-center text-sm text-cyan-600 hover:text-cyan-700 font-medium"
          >
            {mode === 'signup' ? 'Sign in instead' : 'Create a free account'}
          </button>
        </form>

        {/* Admin Login Note */}
        <div className="px-6 pb-4">
          <p className="text-xs text-gray-500 text-center">
            Admin or Client ID login? Use <span className="font-medium">My Account</span> button.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
